import { CookieSettingsLink } from "@/components/legal/CookieConsent";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Lightning, ChatCircleDots, Tag, ArrowsClockwise } from "@phosphor-icons/react";
import { SiStripe, SiQuickbooks } from "react-icons/si";
import { Bank } from "@phosphor-icons/react";
import { Logo } from "@/components/Logo";
import { HeroChart } from "@/components/landing/HeroChart";

const HERO_IMG = "https://images.unsplash.com/photo-1764545973653-94c40d993495?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1600";
const AI_IMG = "https://images.unsplash.com/photo-1710957987034-cea509422852?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200";

const Cta = ({ testid, className = "" }) => (
  <Link to="/sign-up" data-testid={testid}
    className={`inline-flex items-center gap-2 rounded-full bg-[#00FF9D] px-7 py-3.5 font-semibold text-[#050505] transition-[transform,box-shadow] duration-200 hover:scale-[1.03] hover:shadow-[0_0_32px_rgba(0,255,157,0.45)] focus:ring-2 focus:ring-[#00FF9D] focus:outline-none ${className}`}>
    Start 14-Day Free Trial <ArrowRight weight="bold" />
  </Link>
);

const Nav = () => (
  <header className="sticky top-0 z-50 bg-black/60 backdrop-blur-xl border-b border-white/10">
    <div className="mx-auto max-w-7xl px-6 h-16 flex items-center justify-between">
      <Logo />
      <nav className="hidden md:flex items-center gap-8 text-sm text-[#A3A3A3]">
        <a href="#features" data-testid="nav-features-link" className="hover:text-white transition-colors">Features</a>
        <a href="#pricing" data-testid="nav-pricing-link" className="hover:text-white transition-colors">Pricing</a>
      </nav>
      <div className="flex items-center gap-3">
        <Link to="/sign-in" data-testid="nav-signin-link" className="text-sm text-[#A3A3A3] hover:text-white transition-colors">Sign in</Link>
        <Link to="/sign-up" data-testid="nav-trial-btn" className="rounded-full border border-[#00FF9D]/60 px-4 py-2 text-sm text-[#00FF9D] hover:bg-[#00FF9D] hover:text-[#050505] transition-colors">Free trial</Link>
      </div>
    </div>
  </header>
);

const Hero = () => (
  <section className="relative overflow-hidden border-b border-[#2A2A2A] grain">
    <img src={HERO_IMG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
    <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/40" />
    <div className="relative mx-auto max-w-7xl px-6 pt-24 pb-20 lg:pt-32 grid lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-6 fade-up">
        <p className="font-mono text-xs tracking-widest text-[#00FF9D] mb-6 flex items-center gap-2">
          <span className="h-1.5 w-1.5 rounded-full bg-[#00FF9D] animate-pulse" /> STRIPE + BANK + QBO, CALIBRATED
        </p>
        <h1 data-testid="hero-headline" className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.02]">
          Calibrate your runway. Grow with confidence.
        </h1>
        <p className="mt-7 max-w-xl text-base md:text-lg text-[#A3A3A3] leading-relaxed">
          Sync Stripe, QBO and your bank into one precise runway model. Test every growth move with AI before you make it, and know exactly how far your cash will carry you. No more exported Excel models.
        </p>
        <div className="mt-10 flex flex-wrap items-center gap-6">
          <Cta testid="hero-cta-btn" />
          <span className="font-mono text-xs text-[#A3A3A3]">No card required · 3-minute setup</span>
        </div>
      </div>
      <div className="lg:col-span-6 fade-up" style={{ animationDelay: "0.15s" }}>
        <HeroChart />
      </div>
    </div>
  </section>
);

const Features = () => (
  <section id="features" className="mx-auto max-w-7xl px-6 py-24 md:py-32">
    <p className="font-mono text-xs text-[#00FF9D] mb-4">// WHAT YOU GET</p>
    <h2 className="font-display text-3xl md:text-4xl font-semibold max-w-2xl">One calibrated model. Three sources of truth. Room to grow.</h2>
    <div className="mt-16 grid md:grid-cols-6 border-t border-l border-[#2A2A2A]">
      <div data-testid="feature-tri-sync" className="md:col-span-4 p-10 border-r border-b border-[#2A2A2A] hover:bg-[#121212] transition-colors">
        <ArrowsClockwise size={28} className="text-[#00FF9D]" />
        <h3 className="font-display text-2xl mt-6">Real-time Multi-Sync (Stripe + Bank + QBO)</h3>
        <p className="mt-3 text-[#A3A3A3] max-w-lg">Revenue from Stripe, cash from your bank, expenses, A/R and A/P from QuickBooks. Reconciled and recalibrated into one live cash position every time you open the dashboard.</p>
        <div className="mt-10 flex gap-4">
          {[[SiStripe, "Stripe", "#635BFF"], [Bank, "Bank", "#00FF9D"], [SiQuickbooks, "QuickBooks", "#2CA01C"]].map(([I, n, c]) => (
            <div key={n} className="flex items-center gap-2 rounded-full border border-[#2A2A2A] px-4 py-2 text-sm">
              <I size={16} style={{ color: c }} /> {n}
            </div>
          ))}
        </div>
      </div>
      <div data-testid="feature-categorization" className="md:col-span-2 p-10 border-r border-b border-[#2A2A2A] hover:bg-[#121212] transition-colors">
        <Tag size={28} className="text-[#00FF9D]" />
        <h3 className="font-display text-2xl mt-6">Automated Expense Categorization</h3>
        <p className="mt-3 text-[#A3A3A3]">Every vendor sorted into Payroll, Software, Infra and more. Toggle a vendor off to fine-tune burn and see the runway you'd gain.</p>
      </div>
      <div data-testid="feature-chat" className="md:col-span-6 relative overflow-hidden p-10 md:p-14 border-r border-b border-[#2A2A2A]">
        <img src={AI_IMG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent" />
        <div className="relative max-w-xl">
          <ChatCircleDots size={28} className="text-[#9D00FF]" />
          <h3 className="font-display text-2xl md:text-3xl mt-6">AI Scenario Calibrator</h3>
          <p className="mt-3 text-[#A3A3A3]">Ask "What happens if I hire two engineers in Q3?" and watch a purple scenario line recalibrate your runway in seconds. Compare growth plans side by side before you commit.</p>
          <div className="mt-8 rounded-xl border border-[#9D00FF]/40 bg-black/70 p-4 font-mono text-sm">
            <span className="text-[#9D00FF]">&gt;</span> What if we lose our biggest customer next month?
          </div>
        </div>
      </div>
    </div>
  </section>
);

const PLANS = [
  { id: "starter", name: "Starter", price: 29, blurb: "For pre-seed founders calibrating every dollar.",
    perks: ["Multi-Sync dashboard", "Zero Cash Date tracking", "25 AI calibrations / day", "Expense categorization"] },
  { id: "pro", name: "Pro", price: 79, blurb: "For seed+ teams scaling hires and raises.", featured: true,
    perks: ["Everything in Starter", "100 AI calibrations / day", "Vendor cut-off modeling", "Multi-bank accounts", "Priority support"] },
];

const Pricing = () => (
  <section id="pricing" className="border-t border-[#2A2A2A] bg-[#0A0A0A]">
    <div className="mx-auto max-w-7xl px-6 py-24 md:py-32 grid lg:grid-cols-12 gap-12">
      <div className="lg:col-span-4">
        <p className="font-mono text-xs text-[#00FF9D] mb-4">// PRICING</p>
        <h2 className="font-display text-3xl md:text-4xl font-semibold">Cheaper than one hour of a fractional CFO.</h2>
        <p className="mt-5 text-[#A3A3A3]">Both plans start with a 14-day free trial. Cancel anytime.</p>
      </div>
      <div className="lg:col-span-8 grid md:grid-cols-2 gap-6">
        {PLANS.map((p) => (
          <div key={p.id} data-testid={`pricing-card-${p.id}`}
            className={`rounded-2xl border p-8 flex flex-col transition-[transform,border-color] duration-200 hover:-translate-y-1 ${p.featured ? "border-[#00FF9D]/60 bg-[#00FF9D]/[0.03]" : "border-[#2A2A2A] bg-[#121212]"}`}>
            <div className="flex items-center justify-between">
              <h3 className="font-display text-2xl">{p.name}</h3>
              {p.featured && <span className="font-mono text-[10px] rounded-full bg-[#00FF9D] text-[#050505] px-2 py-1">MOST POPULAR</span>}
            </div>
            <p className="mt-2 text-sm text-[#A3A3A3]">{p.blurb}</p>
            <p className="mt-8 font-mono"><span className="text-5xl font-bold">${p.price}</span><span className="text-[#A3A3A3]">/mo</span></p>
            <ul className="mt-8 space-y-3 text-sm flex-1">
              {p.perks.map((k) => <li key={k} className="flex gap-3"><Check className="text-[#00FF9D] mt-0.5" weight="bold" />{k}</li>)}
            </ul>
            <Link to="/sign-up" data-testid={`pricing-${p.id}-cta`}
              className={`mt-10 rounded-full py-3 text-center font-semibold transition-colors ${p.featured ? "bg-[#00FF9D] text-[#050505] hover:bg-white" : "border border-[#2A2A2A] hover:border-[#00FF9D] hover:text-[#00FF9D]"}`}>
              Start 14-Day Free Trial
            </Link>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default function Landing() {
  return (
    <div className="bg-[#050505] min-h-screen" data-testid="landing-page">
      <Nav />
      <Hero />
      <Features />
      <Pricing />
      <section className="mx-auto max-w-7xl px-6 py-24 flex flex-col md:flex-row md:items-end justify-between gap-8">
        <h2 className="font-display text-3xl md:text-5xl font-semibold max-w-2xl">Calibrate your runway. <span className="text-[#00FF9D]">Fuel your growth.</span></h2>
        <Cta testid="footer-cta-btn" />
      </section>
      <footer className="border-t border-[#2A2A2A] py-8 px-6 mx-auto max-w-7xl flex justify-between text-xs font-mono text-[#A3A3A3]">
        <span>© {new Date().getFullYear()} CalibRunway · <a href="/terms" data-testid="footer-terms-link" className="hover:text-white underline">Terms</a> · <CookieSettingsLink testid="footer-cookie-settings" /></span>
        <span className="flex items-center gap-1"><Lightning className="text-[#00FF9D]" /> Built for growing founders</span>
      </footer>
    </div>
  );
}
